import { Injectable, inject, signal, computed } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { doc, onSnapshot, Firestore } from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import {
  normalizeSupportRoleId,
  SUPPORT_ROLES,
  SupportRoleId,
} from '../constants/support-roles.constant';
import { ConstantRole } from '../models/constant-role.model';

export type UserRole = SupportRoleId;

export interface UserContextState {
  role: UserRole | null;
  employeeId: string | null;
  businessId: string | null;
  loaded: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserContextService {
  private authService = inject(AuthService);
  private firestore = inject(Firestore);

  private readonly state = new BehaviorSubject<UserContextState>({
    role: null,
    employeeId: null,
    businessId: null,
    loaded: false,
  });

  readonly role = signal<UserRole | null>(null);
  readonly employeeId = signal<string | null>(null);
  readonly businessId = signal<string | null>(null);
  readonly loaded = signal(false);

  /** Hardcoded support roles (admin / agent). */
  readonly roles = signal<ConstantRole[]>(SUPPORT_ROLES);

  readonly isAdmin = computed(() => this.role() === 'admin');
  readonly isAgent = computed(() => this.role() === 'agent');
  readonly hasOrganizationAccess = computed(() => this.role() !== null);

  private readonly accessRevokedSubject = new Subject<void>();
  readonly accessRevoked$ = this.accessRevokedSubject.asObservable();

  private initInFlight: Promise<void> | null = null;
  private employeeDocUnsubscribe: (() => void) | null = null;

  async initialize(): Promise<void> {
    if (this.initInFlight) return this.initInFlight;
    this.initInFlight = this.performInitialize().finally(() => {
      this.initInFlight = null;
    });
    return this.initInFlight;
  }

  async ensureInitialized(): Promise<void> {
    if (!this.loaded()) await this.initialize();
  }

  private async performInitialize(): Promise<void> {
    this.stopEmployeeDocListener();
    const user = this.authService.getCurrentUser();
    if (!user) {
      this.setState({ role: null, employeeId: null, businessId: null, loaded: true });
      return;
    }

    try {
      const employee = await this.authService.getCurrentEmployee(user.uid);
      if (!employee) {
        this.setState({ role: null, employeeId: null, businessId: null, loaded: true });
        return;
      }

      const roleNorm = normalizeSupportRoleId(employee.role);
      this.setState({
        role: roleNorm,
        employeeId: employee.id,
        businessId: employee.businessId,
        loaded: true,
      });
      this.startEmployeeDocListener(employee.id, employee.businessId);
    } catch {
      this.setState({ role: null, employeeId: null, businessId: null, loaded: true });
    }
  }

  private startEmployeeDocListener(employeeId: string, businessId: string): void {
    if (!employeeId || !businessId) return;
    this.stopEmployeeDocListener();
    const empRef = doc(this.firestore, 'businesses', businessId, 'employees', employeeId);
    this.employeeDocUnsubscribe = onSnapshot(
      empRef,
      (snap) => {
        if (!snap.exists()) {
          this.stopEmployeeDocListener();
          this.setState({ role: null, employeeId: null, businessId: null, loaded: true });
          this.accessRevokedSubject.next();
          return;
        }
        const data = snap.data();
        const role = normalizeSupportRoleId(data['role'] as string | undefined);
        const bId = (data['businessId'] ?? '') as string;
        const prev = this.state.value;
        if (prev.employeeId === employeeId && prev.role === role && prev.businessId === bId) return;
        this.setState({ role, employeeId, businessId: bId, loaded: true });
      },
      (error) => {
        // Avoid uncaught listener errors in dev console when access is revoked or missing.
        console.warn('Employee snapshot listener error:', error);
        this.stopEmployeeDocListener();
        this.setState({ role: null, employeeId: null, businessId: null, loaded: true });
      }
    );
  }

  private stopEmployeeDocListener(): void {
    this.employeeDocUnsubscribe?.();
    this.employeeDocUnsubscribe = null;
  }

  private setState(partial: Partial<UserContextState>): void {
    const next: UserContextState = { ...this.state.value, ...partial };
    this.state.next(next);
    this.role.set(next.role);
    this.employeeId.set(next.employeeId);
    this.businessId.set(next.businessId);
    this.loaded.set(next.loaded);
  }

  refresh(): void {
    this.stopEmployeeDocListener();
    this.setState({ loaded: false });
    void this.initialize();
  }
}
